import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Download,
  ExternalLink,
  FileSignature,
  Flame,
  Loader2,
  Lock,
  PhoneCall,
  Plus,
  RefreshCw,
  ShieldAlert,
  Stethoscope,
  Trash2,
  Upload,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from './Button';
import { PoortwachterTijdlijn, INKOOP_ARBEIDSDESKUNDIGE_LINK } from './PoortwachterTijdlijn';
import { ZiekmeldingWizard } from './ZiekmeldingWizard';
import { QK_POORTWACHTER, SigningWrapperModal, UploadExternModal, VerzegelingsBadges } from './PoortwachterSigningWrapper';
import { UwvMeldingModal } from './UwvMeldingModal';
import { UwvConnectorKnop } from './UwvConnectorInstellingen';
import { useNaamGrendel } from '../utils/naamGrendel';
import {
  boekPoortwachterProfessional,
  dienEerstejaarsIn,
  downloadUwvDossier,
  getDossierAudit,
  getEerstejaarsPrefill,
  getPoortwachterCases,
  rondPoortwachterMijlpaalAf,
  updatePoortwachterStatus,
  verifieerZiekmelding,
  type DoelBehaald,
  type EerstejaarsInvoer,
  type PoortwachterCase,
  type PoortwachterDocumentMeta,
  type PoortwachterOverzicht,
  type PoortwachterStatus,
  type WvpStap,
} from '../services/lopendeTrajectenApi';
import { ZipWachtwoordDialoog } from './ZipWachtwoordDialoog';

/**
 * Tabel 2 — Wet Verbetering Poortwachter: het 8-koloms grid.
 *
 *  1 Identificatie-Grendel   MDW-code + lokale naam (Naam-Grendel, alleen in deze browser)
 *  2 Tijdas                  eerste ziektedag + 🔥 live dagenteller / week
 *  3 Toegangs-Scope          vestiging ➔ afdeling (+ lijnmanager)
 *  4 State-Machine           actuele WVP-stap (8 enums)
 *  5 Poortwachter-Klok       eerstvolgende wettelijke deadline, 🟢/🟡/🔴
 *  6 Risicometer             € 0 · € 45.000 (MKB) · € 65.000 (Enterprise)
 *  7 Verzegelings-Status     Arts ➔ Werkgever ➔ Medewerker + Audit-Certificaat
 *  8 Repareer-Grendel        contextuele actieknop per WVP-stap
 */

const QK_OVERZICHT = ['lopende-trajecten', 'overzicht'];
const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('nl-NL') : '—');
const apiMessage = (err: any, fallback: string): string => err?.response?.data?.message || err?.message || fallback;
const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';
const thClass = 'px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap';
const tdClass = 'px-3 py-2 text-sm text-gray-800 align-top';

const WVP_STAP_LABEL: Record<WvpStap, string> = {
  ZIEKMELDING_PENDING: 'Ziekmelding · verificatie',
  PROBLEMANALYSE_W6: 'Probleemanalyse (wk 6)',
  PLAN_VAN_AANPAK_W8: 'Plan van Aanpak (wk 8)',
  CONTACTMOMENT_6W: 'Contactmoment (6-wekelijks)',
  UWV_MELDING_W42: 'UWV-melding (wk 42)',
  EERSTEJAARSEVALUATIE_W52: 'Eerstejaarsevaluatie (wk 52)',
  WIA_AANVRAAG_W87: 'WIA-aanvraag (wk 87)',
  EINDDOSSIER_W91: 'WVP-einddossier (wk 91)',
};

const PW_STATUS: Record<PoortwachterStatus, string> = {
  CASE_OPENED: 'CASE_OPENED',
  PROBLEM_ANALYSIS_DUE: 'PROBLEM_ANALYSIS_DUE',
  PLAN_OF_ACTION_ACTIVE: 'PLAN_OF_ACTION_ACTIVE',
  FIRST_YEAR_EVALUATION: 'FIRST_YEAR_EVALUATION',
  WIA_APPLICATION: 'WIA_APPLICATION',
  CLOSED: 'CLOSED',
};

const Laden: React.FC = () => (
  <div className="flex items-center justify-center py-10 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Laden…</div>
);

// ─── Kolom 5: de klok ─────────────────────────────────────────────────────────

const KlokCel: React.FC<{ c: PoortwachterCase }> = ({ c }) => {
  const k = c.klok;
  if (c.status === 'CLOSED') return <span className="text-xs text-gray-500">Hersteld op {fmtDate(c.hersteld_op)}</span>;
  if (!k.mijlpaal_key) return <span className="text-xs text-green-700">🟢 Alle mijlpalen voltooid</span>;
  const stijl = { groen: 'bg-green-50 border-green-200 text-green-900', geel: 'bg-yellow-50 border-yellow-300 text-yellow-900', rood: 'bg-red-50 border-red-400 text-red-900' }[k.kleur];
  const bol = { groen: '🟢', geel: '🟡', rood: '🔴' }[k.kleur];
  return (
    <div className={`inline-block p-2 rounded-lg border text-xs ${stijl}`}>
      <div className="font-semibold">{bol} {k.label}</div>
      <div>{fmtDate(k.datum)}</div>
      <div className="font-semibold">
        {k.dagen == null ? '' : k.dagen < 0 ? `🔴 ${-k.dagen} dagen overtijd` : k.kleur === 'geel' ? `⏳ Nog ${k.dagen} dagen` : `On track · nog ${k.dagen} d`}
      </div>
    </div>
  );
};

// ─── Week 52: Eerstejaarsevaluatie-wizard ─────────────────────────────────────

const LEGE_AFSPRAAK: EerstejaarsInvoer['afspraken_tweede_jaar'][number] = { actie: '', wie: 'werkgever', wanneer: '' };

const EerstejaarsFormulier: React.FC<{ c: PoortwachterCase; onSluit: () => void }> = ({ c, onSluit }) => {
  const qc = useQueryClient();
  const { data: p, isLoading } = useQuery({ queryKey: [...QK_POORTWACHTER, 'eerstejaars', c.id], queryFn: () => getEerstejaarsPrefill(c.id) });
  const [form, setForm] = useState<EerstejaarsInvoer>({ doel_behaald: 'deels', terugblik: '', reintegratievisie_medewerker: '', visie_werkgever: '', afspraken_tweede_jaar: [{ ...LEGE_AFSPRAAK }], spoor2_overwogen: false });
  const m = useMutation({
    mutationFn: () => dienEerstejaarsIn(c.id, form),
    onSuccess: (r) => {
      toast.success(r.sms_verzonden ? 'Eerstejaarsevaluatie getekend; SMS naar de medewerker verstuurd.' : `Eerstejaarsevaluatie getekend. ${r.reden ?? ''}`);
      qc.invalidateQueries({ queryKey: QK_POORTWACHTER });
      onSluit();
    },
    onError: (e: any) => toast.error(apiMessage(e, 'Indienen mislukt')),
  });
  const zet = (i: number, patch: Partial<EerstejaarsInvoer['afspraken_tweede_jaar'][number]>) =>
    setForm((f) => ({ ...f, afspraken_tweede_jaar: f.afspraken_tweede_jaar.map((a, j) => (j === i ? { ...a, ...patch } : a)) }));
  const compleet = form.terugblik.trim() && form.reintegratievisie_medewerker.trim() && form.visie_werkgever.trim() && form.afspraken_tweede_jaar.every((a) => a.actie.trim() && a.wanneer);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" role="dialog" aria-modal="true" aria-label="Eerstejaarsevaluatie">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><RefreshCw className="w-4 h-4 text-amber-600" /> Eerstejaarsevaluatie · {c.case_code}</h3>
          <button type="button" onClick={onSluit} aria-label="Sluiten" className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        {isLoading || !p ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : (
          <div className="p-5 space-y-4">
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 p-3 rounded-lg bg-gray-50 border border-gray-200 text-xs">
              {[['Werkgever', p.werkgever], ['Case', `${p.case_code}${p.pseudoniem ? ` · ${p.pseudoniem}` : ''}`], ['Eerste ziektedag', fmtDate(p.eerste_ziektedag)], ['Wettelijke deadline', fmtDate(p.deadline)]].map(([k, v]) => (
                <div key={k as string}><dt className="text-gray-500">{k}</dt><dd className="font-medium text-gray-900">{v || '—'}</dd></div>
              ))}
            </dl>
            {p.ingediend && (
              <div className={`p-3 rounded-lg text-sm border ${p.ingediend.status === 'ondertekend' ? 'bg-green-50 border-green-200 text-green-900' : 'bg-orange-50 border-orange-200 text-orange-900'}`}>
                {p.ingediend.status === 'ondertekend' ? `Tweezijdig ondertekend op ${fmtDate(p.ingediend.ondertekend_op)}.` : `Door de werkgever getekend op ${fmtDate(p.ingediend.werkgever_ondertekend_op)} — wacht op de SMS-2FA van de medewerker. Opnieuw indienen overschrijft deze versie.`}
              </div>
            )}
            {!p.kan_indienen && p.ingediend?.status !== 'ondertekend' && (
              <div className="p-3 rounded-lg text-sm bg-orange-50 border border-orange-200 text-orange-900">Koppel eerst een bedrijfsarts aan deze case (boekingsknop week 6).</div>
            )}
            <div>
              <label className={labelClass} htmlFor="ej-doel">1 · Is het re-integratiedoel uit het Plan van Aanpak behaald?</label>
              <select id="ej-doel" className={inputClass} value={form.doel_behaald} onChange={(e) => setForm((f) => ({ ...f, doel_behaald: e.target.value as DoelBehaald }))}>
                {(Object.keys(p.doel_opties) as DoelBehaald[]).map((k) => <option key={k} value={k}>{p.doel_opties[k]}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="ej-terug">2 · Terugblik op het eerste ziektejaar</label>
              <textarea id="ej-terug" rows={3} className={inputClass} value={form.terugblik} onChange={(e) => setForm((f) => ({ ...f, terugblik: e.target.value }))} />
            </div>
            <div>
              <label className={labelClass} htmlFor="ej-visie-mw">3 · Re-integratievisie van de medewerker * <span className="font-normal text-gray-500">(verplicht — in de woorden van de medewerker)</span></label>
              <textarea id="ej-visie-mw" rows={3} className={`${inputClass} border-amber-400`} value={form.reintegratievisie_medewerker} onChange={(e) => setForm((f) => ({ ...f, reintegratievisie_medewerker: e.target.value }))} />
            </div>
            <div>
              <label className={labelClass} htmlFor="ej-visie-wg">4 · Visie van de werkgever</label>
              <textarea id="ej-visie-wg" rows={3} className={inputClass} value={form.visie_werkgever} onChange={(e) => setForm((f) => ({ ...f, visie_werkgever: e.target.value }))} />
            </div>
            <div>
              <div className={labelClass}>5 · Afspraken tweede ziektejaar</div>
              <div className="space-y-2">
                {form.afspraken_tweede_jaar.map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <input aria-label={`Afspraak ${i + 1}`} placeholder="Wat wordt er gedaan?" className={`${inputClass} col-span-12 md:col-span-6`} value={a.actie} onChange={(e) => zet(i, { actie: e.target.value })} />
                    <select aria-label="Door wie" className={`${inputClass} col-span-5 md:col-span-3`} value={a.wie} onChange={(e) => zet(i, { wie: e.target.value as typeof a.wie })}>
                      <option value="werkgever">Werkgever</option><option value="werknemer">Werknemer</option><option value="bedrijfsarts">Bedrijfsarts</option><option value="arbeidsdeskundige">Arbeidsdeskundige</option><option value="anders">Anders</option>
                    </select>
                    <input aria-label="Uiterlijk op" type="date" className={`${inputClass} col-span-5 md:col-span-2`} value={a.wanneer} onChange={(e) => zet(i, { wanneer: e.target.value })} />
                    <button type="button" aria-label="Afspraak verwijderen" disabled={form.afspraken_tweede_jaar.length === 1} className="col-span-2 md:col-span-1 flex items-center justify-center text-gray-400 hover:text-red-600 disabled:opacity-30" onClick={() => setForm((f) => ({ ...f, afspraken_tweede_jaar: f.afspraken_tweede_jaar.filter((_, j) => j !== i) }))}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="mt-2 text-xs text-blue-700 hover:underline inline-flex items-center" onClick={() => setForm((f) => ({ ...f, afspraken_tweede_jaar: [...f.afspraken_tweede_jaar, { ...LEGE_AFSPRAAK }] }))}>
                <Plus className="w-3 h-3 mr-1" /> Afspraak toevoegen
              </button>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input type="checkbox" checked={form.spoor2_overwogen} onChange={(e) => setForm((f) => ({ ...f, spoor2_overwogen: e.target.checked }))} /> 6 · Spoor 2 (ander werk bij een andere werkgever) is overwogen
            </label>
            <div className="flex items-center justify-between gap-3 pt-3 border-t">
              <p className="text-xs text-gray-500">U tekent direct als werkgever; de medewerker ontvangt een SMS en tekent met de 6-cijferige code.</p>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={onSluit}>Annuleren</Button>
                <Button size="sm" disabled={!p.kan_indienen || !compleet || m.isPending} onClick={() => m.mutate()}>
                  <FileSignature className="w-3.5 h-3.5 mr-1" /> Teken &amp; verstuur naar medewerker
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Week 87-91: dossier-audit + einddossier ──────────────────────────────────

const DossierAuditModal: React.FC<{ c: PoortwachterCase; onSluit: () => void }> = ({ c, onSluit }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: [...QK_POORTWACHTER, 'audit', c.id], queryFn: () => getDossierAudit(c.id) });
  const [exporteert, setExporteert] = useState(false);
  const [zipWachtwoord, setZipWachtwoord] = useState<string | null>(null);
  const wia = c.deadlines.find((d) => d.key === 'wia_aanvraag' && d.status === 'OPEN');
  const afrondM = useMutation({
    mutationFn: () => rondPoortwachterMijlpaalAf(c.id, wia!.id, { afronden: true }),
    onSuccess: () => {
      toast.success('WIA-aanvraag geregistreerd');
      qc.invalidateQueries({ queryKey: QK_POORTWACHTER });
    },
    onError: (e: any) => toast.error(apiMessage(e, 'Afronden mislukt')),
  });
  const exportKan = c.week_nu >= c.dossier_export_vanaf_week || c.status === 'WIA_APPLICATION';
  const exporteer = async () => {
    setExporteert(true);
    try {
      setZipWachtwoord(await downloadUwvDossier(c.id, c.case_code));
      toast.success('WVP-einddossier (ZIP) gedownload');
    } catch (e: any) {
      toast.error(apiMessage(e, 'Exporteren mislukt'));
    } finally {
      setExporteert(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" role="dialog" aria-modal="true" aria-label="Dossier-audit">
      {zipWachtwoord && <ZipWachtwoordDialoog wachtwoord={zipWachtwoord} onSluit={() => setZipWachtwoord(null)} />}
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-blue-600" /> Dossier-audit &amp; WVP-einddossier · {c.case_code}</h3>
          <button type="button" onClick={onSluit} aria-label="Sluiten" className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        {isLoading || !data ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : (
          <div className="p-5 space-y-4">
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {data.checks.map((ch) => (
                <li key={ch.key} className="flex items-start gap-2 p-2.5 text-sm">
                  {ch.ok ? <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />}
                  <div>
                    <div className={ch.ok ? 'text-gray-800' : 'text-red-800 font-medium'}>{ch.label}</div>
                    {ch.detail && <div className="text-xs text-gray-500">{ch.detail}</div>}
                  </div>
                </li>
              ))}
            </ul>
            <div className={`p-3 rounded-lg text-sm border ${data.compleet ? 'bg-green-50 border-green-200 text-green-900' : 'bg-orange-50 border-orange-200 text-orange-900'}`}>
              {data.compleet
                ? 'Het re-integratieverslag (RIV) is compleet en volledig verzegeld. U kunt het einddossier genereren voor de WIA-aanvraag.'
                : 'Het dossier is nog niet compleet. Het UWV kan bij ontbrekende of onverzegelde stukken een loonsanctie opleggen; herstel eerst de rode punten. Exporteren kan wel (gelogd).'}
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
              <Button variant={data.compleet ? 'primary' : 'outline'} disabled={!exportKan || exporteert} title={exportKan ? undefined : `Beschikbaar vanaf week ${c.dossier_export_vanaf_week}`} onClick={exporteer}>
                {exporteert ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} 📋 Genereer &amp; verzegel WVP-einddossier (ZIP)
              </Button>
              {wia && (
                <Button variant="outline" disabled={afrondM.isPending} onClick={() => afrondM.mutate()}>
                  <Check className="w-4 h-4 mr-1" /> WIA-aanvraag ingediend
                </Button>
              )}
              <span className="text-[11px] text-gray-500">ZIP met alle verzegelde documenten, originele PDF's en manifest.json (SHA-256). Elke export wordt gelogd.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Kolom 8: de Repareer-Grendel ─────────────────────────────────────────────

type Actie =
  | { soort: 'verifieer' }
  | { soort: 'teken'; doc: PoortwachterDocumentMeta }
  | { soort: 'boek_arts' }
  | { soort: 'upload'; soortDefault: string }
  | { soort: 'pva' }
  | { soort: 'contact'; mijlpaalId: string }
  | { soort: 'uwv' }
  | { soort: 'eerstejaars' }
  | { soort: 'einddossier' };

/** Welke knop hoort bij de actuele WVP-stap? (muteert mee met de status) */
function bepaalActie(c: PoortwachterCase): Actie {
  const docs = c.documenten.lijst;
  const teTekenen = docs.find((d) => d.status === 'ter_ondertekening');
  switch (c.wvp_stap) {
    case 'ZIEKMELDING_PENDING':
      return { soort: 'verifieer' };
    case 'PROBLEMANALYSE_W6': {
      const pa = docs.find((d) => d.soort === 'probleemanalyse' && d.status === 'ter_ondertekening');
      if (pa) return { soort: 'teken', doc: pa };
      if (!c.bedrijfsarts_dossier_id) return { soort: 'boek_arts' };
      return { soort: 'upload', soortDefault: 'probleemanalyse' };
    }
    case 'PLAN_VAN_AANPAK_W8':
      return teTekenen ? { soort: 'teken', doc: teTekenen } : { soort: 'pva' };
    case 'CONTACTMOMENT_6W': {
      if (teTekenen) return { soort: 'teken', doc: teTekenen };
      const ev = c.deadlines.find((d) => d.key === 'periodieke_evaluatie' && d.status === 'OPEN');
      return ev ? { soort: 'contact', mijlpaalId: ev.id } : { soort: 'upload', soortDefault: 'periodieke_evaluatie' };
    }
    case 'UWV_MELDING_W42':
      return teTekenen ? { soort: 'teken', doc: teTekenen } : { soort: 'uwv' };
    case 'EERSTEJAARSEVALUATIE_W52':
      return teTekenen ? { soort: 'teken', doc: teTekenen } : { soort: 'eerstejaars' };
    default:
      return teTekenen ? { soort: 'teken', doc: teTekenen } : { soort: 'einddossier' };
  }
}

// ─── De rij ───────────────────────────────────────────────────────────────────

const PoortwachterRij: React.FC<{
  c: PoortwachterCase;
  overzicht: PoortwachterOverzicht;
  naam: { naam: string; contact: string } | null;
  startOpen: boolean;
}> = ({ c, overzicht, naam, startOpen }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(startOpen);
  const [modal, setModal] = useState<null | { soort: 'teken'; docId: string } | { soort: 'upload'; soortDefault: string } | { soort: 'eerstejaars' } | { soort: 'einddossier' } | { soort: 'uwv' }>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: QK_POORTWACHTER });
    qc.invalidateQueries({ queryKey: QK_OVERZICHT });
  };
  const statusM = useMutation({
    mutationFn: (status: PoortwachterStatus) => updatePoortwachterStatus(c.id, { status }),
    onSuccess: () => { toast.success('Status bijgewerkt'); refresh(); },
    onError: (e: any) => toast.error(apiMessage(e, 'Bijwerken mislukt')),
  });
  const boekM = useMutation({
    mutationFn: (rol: 'bedrijfsarts' | 'arbeidsdeskundige') => boekPoortwachterProfessional(c.id, rol),
    onSuccess: (r) => { window.open(r.url, '_blank', 'noopener'); toast.success('Boekingslink geopend'); refresh(); },
    onError: (e: any) => toast.error(apiMessage(e, 'Boeken mislukt')),
  });
  const verifieerM = useMutation({
    mutationFn: () => verifieerZiekmelding(c.id),
    onSuccess: () => { toast.success('Ziekmelding geverifieerd — de Poortwachter-klok loopt'); refresh(); },
    onError: (e: any) => toast.error(apiMessage(e, 'Verifiëren mislukt')),
  });
  const contactM = useMutation({
    mutationFn: (mijlpaalId: string) => rondPoortwachterMijlpaalAf(c.id, mijlpaalId, { afronden: true, checklist: ['voortgang_besproken', 'afspraken_passend', 'belastbaarheid_actueel', 'bijstelling_beoordeeld', 'vastgelegd'].map((key) => ({ key, gedaan: true })) }),
    onSuccess: () => { toast.success('Gesprek gelogd — 42-dagen klok gereset'); refresh(); },
    onError: (e: any) => toast.error(apiMessage(e, 'Loggen mislukt')),
  });

  const gesloten = c.status === 'CLOSED';
  const actie = bepaalActie(c);
  const rood = c.klok.kleur === 'rood' && !gesloten;
  const bezig = verifieerM.isPending || boekM.isPending || contactM.isPending;

  const knop = (() => {
    if (gesloten) return null;
    const p = { size: 'sm' as const, disabled: bezig, className: rood ? 'ring-2 ring-red-400' : undefined };
    switch (actie.soort) {
      case 'verifieer':
        return <Button {...p} onClick={() => verifieerM.mutate()}><PhoneCall className="w-3.5 h-3.5 mr-1" /> 📞 Verifieer ziekmelding</Button>;
      case 'teken':
        return <Button {...p} onClick={() => setModal({ soort: 'teken', docId: actie.doc.id })}><FileSignature className="w-3.5 h-3.5 mr-1" /> 📤 Wrapper: teken {actie.doc.soort_label.split(' (')[0]}</Button>;
      case 'boek_arts':
        return <Button {...p} disabled={bezig || !overzicht.beschikbaar.bedrijfsarts} title={overzicht.beschikbaar.bedrijfsarts ? 'Week 6: probleemanalyse' : 'Geen bedrijfsarts in uw pool'} onClick={() => boekM.mutate('bedrijfsarts')}><Stethoscope className="w-3.5 h-3.5 mr-1" /> Boek bedrijfsarts (wk 6)</Button>;
      case 'upload':
        return <Button {...p} variant="outline" onClick={() => setModal({ soort: 'upload', soortDefault: actie.soortDefault })}><Upload className="w-3.5 h-3.5 mr-1" /> Upload PDF arbo-arts</Button>;
      case 'pva':
        return <Button {...p} onClick={() => setOpen(true)}><FileSignature className="w-3.5 h-3.5 mr-1" /> 📝 Start Plan van Aanpak Wizard</Button>;
      case 'contact':
        return <Button {...p} onClick={() => contactM.mutate(actie.mijlpaalId)}><Users className="w-3.5 h-3.5 mr-1" /> 👥 Log gesprek &amp; reset timer</Button>;
      case 'uwv':
        return <Button {...p} variant="danger" onClick={() => setModal({ soort: 'uwv' })}><ExternalLink className="w-3.5 h-3.5 mr-1" /> 📞 Verzend digitale UWV-melding</Button>;
      case 'eerstejaars':
        return (
          <div className="flex flex-col gap-1">
            <Button {...p} onClick={() => setModal({ soort: 'eerstejaars' })}><RefreshCw className="w-3.5 h-3.5 mr-1" /> 🔄 Start eerstejaarsevaluatie</Button>
            {overzicht.beschikbaar.arbeidsdeskundige ? (
              <Button size="sm" variant="outline" disabled={bezig} onClick={() => boekM.mutate('arbeidsdeskundige')}><CalendarClock className="w-3.5 h-3.5 mr-1" /> Boek arbeidsdeskundige</Button>
            ) : (
              <Link to={INKOOP_ARBEIDSDESKUNDIGE_LINK} className="inline-flex items-center justify-center px-2 py-1.5 rounded-lg text-xs font-semibold bg-amber-400 border border-amber-500 text-amber-950">Inkoop arbeidsdeskundige</Link>
            )}
          </div>
        );
      case 'einddossier':
        return <Button {...p} onClick={() => setModal({ soort: 'einddossier' })}><ClipboardCheck className="w-3.5 h-3.5 mr-1" /> 📋 Valideer &amp; genereer WVP-einddossier</Button>;
    }
  })();

  return (
    <>
      <tr className={`hover:bg-gray-50 ${gesloten ? 'opacity-60' : ''} ${rood ? 'bg-red-50/60' : ''}`}>
        {/* 1 Identificatie-Grendel */}
        <td className={tdClass}>
          {naam ? (
            <div className="font-semibold text-gray-900 flex items-center gap-1" title="Alleen zichtbaar in deze browser (Naam-Grendel)"><Lock className="w-3 h-3 text-gray-400" /> {naam.naam}</div>
          ) : (
            <div className="text-xs text-gray-400 italic">naam niet lokaal bekend</div>
          )}
          <div className="font-mono text-xs font-semibold text-gray-700">{c.pseudoniem ?? '—'}</div>
          <div className="font-mono text-[10px] text-gray-400">{c.case_code}</div>
        </td>
        {/* 2 Tijdas */}
        <td className={tdClass}>
          <div>{fmtDate(c.ziekmelding_datum)}</div>
          {!gesloten && <div className="text-xs font-semibold text-red-700 flex items-center gap-1"><Flame className="w-3 h-3" /> {c.dagen_verzuim} dagen verzuim · week {c.week_nu}</div>}
        </td>
        {/* 3 Toegangs-Scope */}
        <td className={tdClass}>
          <div>{c.vestiging ?? <span className="text-gray-400">Alle vestigingen</span>} <span className="text-gray-400">➔</span> {c.afdeling ?? '—'}</div>
          {c.lijnmanager && <div className="text-xs text-gray-500">Lijnmanager: {c.lijnmanager.naam}</div>}
        </td>
        {/* 4 State-Machine */}
        <td className={tdClass}>
          <div className="font-mono text-[11px] font-semibold text-gray-900">{c.wvp_stap}</div>
          <div className="text-xs text-gray-600">{WVP_STAP_LABEL[c.wvp_stap]}</div>
          <select className="mt-1 text-[10px] border border-gray-300 rounded px-1 py-0.5 max-w-[190px] text-gray-500" value={c.status} disabled={statusM.isPending} onChange={(e) => statusM.mutate(e.target.value as PoortwachterStatus)} title="Case-status (statusmachine)">
            {(Object.keys(PW_STATUS) as PoortwachterStatus[]).map((s) => <option key={s} value={s}>{PW_STATUS[s]}</option>)}
          </select>
        </td>
        {/* 5 Klok */}
        <td className={tdClass}>
          <KlokCel c={c} />
          <button type="button" className="mt-1 block text-xs text-blue-700 hover:underline" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />} Tijdlijn · 7 mijlpalen
          </button>
        </td>
        {/* 6 Risicometer */}
        <td className={tdClass}>
          {c.risico_bedrag > 0 ? (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-600 text-white text-sm font-bold"><ShieldAlert className="w-4 h-4" /> 🔴 {euro.format(c.risico_bedrag)}</div>
          ) : (
            <div className="text-sm text-green-700 font-semibold">🟢 € 0,-</div>
          )}
        </td>
        {/* 7 Verzegelings-status */}
        <td className={tdClass}>
          {c.documenten.lijst.length === 0 ? (
            <span className="text-xs text-gray-400">Nog geen documenten</span>
          ) : (
            <div className="space-y-1.5">
              {c.documenten.lijst.map((doc) => <VerzegelingsBadges key={doc.id} c={c} doc={doc} onTeken={(d) => setModal({ soort: 'teken', docId: d.id })} />)}
            </div>
          )}
        </td>
        {/* 8 Repareer-Grendel */}
        <td className={tdClass}>{knop}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={8} className="p-0">
            <PoortwachterTijdlijn c={c} uwvUrl={overzicht.uwv_portaal_url} arbeidsdeskundigeInPool={overzicht.beschikbaar.arbeidsdeskundige} onBoekArbeidsdeskundige={() => boekM.mutate('arbeidsdeskundige')} />
          </td>
        </tr>
      )}
      {modal?.soort === 'teken' && <SigningWrapperModal c={c} docId={modal.docId} onSluit={() => setModal(null)} />}
      {modal?.soort === 'upload' && <UploadExternModal c={c} soortDefault={modal.soortDefault} onSluit={() => setModal(null)} />}
      {modal?.soort === 'eerstejaars' && <EerstejaarsFormulier c={c} onSluit={() => setModal(null)} />}
      {modal?.soort === 'einddossier' && <DossierAuditModal c={c} onSluit={() => setModal(null)} />}
      {modal?.soort === 'uwv' && <UwvMeldingModal c={c} onSluit={() => setModal(null)} />}
    </>
  );
};

// ─── De tab ───────────────────────────────────────────────────────────────────

export const PoortwachterDatatable: React.FC = () => {
  const [wizard, setWizard] = useState(false);
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const { data, isLoading } = useQuery({ queryKey: QK_POORTWACHTER, queryFn: getPoortwachterCases });
  const { namen, bewaar, beschikbaar: grendel } = useNaamGrendel(data?.employer_id);
  if (isLoading || !data) return <Laden />;
  const cases = data.cases;
  const totRood = cases.filter((c) => c.risico_bedrag > 0).reduce((s, c) => s + c.risico_bedrag, 0);
  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-start gap-3">
        <div className="flex-1 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-900">
          <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            <strong>Naam-Grendel (Zero-Knowledge):</strong> namen staan uitsluitend versleuteld in deze browser; de database en de professionals kennen alleen de code MDW-xxxxx-X.
            Medische inhoud blijft tussen bedrijfsarts en medewerker; u tekent akkoordverklaringen via de Digital Signing Wrapper (SMS-2FA).
            {data.risicometer.fte == null && ' Vul het aantal medewerkers in uw organisatieprofiel in voor een juiste Loonsanctie-Risicometer (nu MKB-tarief).'}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <UwvConnectorKnop />
          <Button onClick={() => setWizard(true)}><Plus className="w-4 h-4 mr-1" /> ➕ Nieuwe ziekmelding geregistreerd</Button>
        </div>
      </div>
      {!grendel && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> De lokale Naam-Grendel werkt alleen via https; namen kunnen nu niet lokaal worden bewaard.
        </div>
      )}
      {(!data.beschikbaar.bedrijfsarts || !data.beschikbaar.arbeidsdeskundige) && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            {!data.beschikbaar.bedrijfsarts && 'Geen bedrijfsarts in uw bedrijfspool. '}
            {!data.beschikbaar.arbeidsdeskundige && 'Geen arbeidsdeskundige in uw bedrijfspool. '}
            Voeg de discipline toe via <Link to="/employer/mijn-belagom?tab=pools" className="underline">Pool-Configurator</Link>.
          </span>
        </div>
      )}
      {totRood > 0 && (
        <div className="p-3 rounded-lg bg-red-600 text-white text-sm font-semibold flex items-center gap-2">
          <ShieldAlert className="w-4 h-4" /> Totale UWV-loonsanctie-liability op dit moment: {euro.format(totRood)} ({cases.filter((c) => c.risico_bedrag > 0).length} case(s) in de Critical Zone)
        </div>
      )}
      {wizard && (
        <ZiekmeldingWizard
          employerId={data.employer_id}
          onBewaarNaam={bewaar}
          onSluit={() => setWizard(false)}
          onKlaar={() => {
            setWizard(false);
            qc.invalidateQueries({ queryKey: QK_POORTWACHTER });
            qc.invalidateQueries({ queryKey: QK_OVERZICHT });
          }}
        />
      )}
      {cases.length === 0 ? (
        <div className="text-center py-10 text-sm text-gray-500">Geen actieve ziekmeldingen binnen uw Toegangs-Scope.</div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>1 · Case &amp; medewerker</th>
                <th className={thClass}>2 · Start &amp; verzuimduur</th>
                <th className={thClass}>3 · Vestiging ➔ afdeling</th>
                <th className={thClass}>4 · WVP-stap</th>
                <th className={thClass}>5 · Poortwachter-klok</th>
                <th className={thClass}>6 · Loonsanctie-risico</th>
                <th className={thClass}>7 · Verzegeling</th>
                <th className={thClass}>8 · Actie</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cases.map((c) => (
                <PoortwachterRij key={c.id} c={c} overzicht={data} naam={c.medewerker_hash ? namen[c.medewerker_hash] ?? null : null} startOpen={params.get('case') === c.id} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PoortwachterDatatable;
